// icons-match level format.
//
// IconsMatchLevel  — committed JSON shape stored in GitHub.
// EditorDraft      — editor working state stored in localStorage.
//                    May have neutral tiles (goalId === null).

export interface LevelTile {
  id: string;
  imageId: string;
}

export interface LevelGoal {
  id: string;
  size: number;
  title: string;
  tiles: LevelTile[];
}

export interface LevelColumn {
  id: string;
  tiles: string[]; // ordered tile IDs, first = topmost (delivered first)
}

export interface IconsMatchLevel {
  id: string;
  columnCount: number;
  traySize: number;
  goals: LevelGoal[];
  columns: LevelColumn[];
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_COLUMN_COUNT = 5;

export function defaultLevel(id = 'level_1'): IconsMatchLevel {
  return {
    id,
    columnCount: DEFAULT_COLUMN_COUNT,
    traySize: DEFAULT_COLUMN_COUNT,
    goals: [],
    columns: Array.from({ length: DEFAULT_COLUMN_COUNT }, (_, i) => ({
      id: `column_${i + 1}`,
      tiles: [],
    })),
  };
}

// ---------------------------------------------------------------------------
// Structural validation (for loading / importing JSON)
// ---------------------------------------------------------------------------

export function validateLevel(
  data: unknown,
): { ok: true; level: IconsMatchLevel } | { ok: false; error: string } {
  if (!data || typeof data !== 'object') return { ok: false, error: 'Not an object' };
  const d = data as Record<string, unknown>;

  if (typeof d['id'] !== 'string') return { ok: false, error: 'id must be a string' };
  if (typeof d['columnCount'] !== 'number' || d['columnCount'] < 1)
    return { ok: false, error: 'columnCount must be a positive number' };
  if (typeof d['traySize'] !== 'number' || d['traySize'] < 1)
    return { ok: false, error: 'traySize must be a positive number' };

  if (!Array.isArray(d['goals'])) return { ok: false, error: 'goals must be an array' };
  if (!Array.isArray(d['columns'])) return { ok: false, error: 'columns must be an array' };

  const goals: LevelGoal[] = [];
  for (const g of d['goals'] as unknown[]) {
    const r = parseGoal(g);
    if (!r.ok) return r;
    goals.push(r.goal);
  }

  const columns: LevelColumn[] = [];
  for (const c of d['columns'] as unknown[]) {
    const r = parseColumn(c);
    if (!r.ok) return r;
    columns.push(r.column);
  }

  return {
    ok: true,
    level: {
      id: d['id'] as string,
      columnCount: d['columnCount'] as number,
      traySize: d['traySize'] as number,
      goals,
      columns,
    },
  };
}

function parseGoal(data: unknown): { ok: true; goal: LevelGoal } | { ok: false; error: string } {
  if (!data || typeof data !== 'object') return { ok: false, error: 'Goal must be an object' };
  const d = data as Record<string, unknown>;

  if (typeof d['id'] !== 'string') return { ok: false, error: 'Goal id must be a string' };
  if (typeof d['size'] !== 'number' || d['size'] < 1)
    return { ok: false, error: `Goal ${d['id']}: size must be a positive number` };
  if (typeof d['title'] !== 'string')
    return { ok: false, error: `Goal ${d['id']}: title must be a string` };
  if (!Array.isArray(d['tiles']))
    return { ok: false, error: `Goal ${d['id']}: tiles must be an array` };

  const tiles: LevelTile[] = [];
  for (const t of d['tiles'] as unknown[]) {
    if (!t || typeof t !== 'object') return { ok: false, error: `Goal ${d['id']}: tile must be an object` };
    const td = t as Record<string, unknown>;
    if (typeof td['id'] !== 'string')
      return { ok: false, error: `Goal ${d['id']}: tile id must be a string` };
    if (typeof td['imageId'] !== 'string')
      return { ok: false, error: `Goal ${d['id']}: tile imageId must be a string` };
    tiles.push({ id: td['id'] as string, imageId: td['imageId'] as string });
  }

  return { ok: true, goal: { id: d['id'] as string, size: d['size'] as number, title: d['title'] as string, tiles } };
}

function parseColumn(data: unknown): { ok: true; column: LevelColumn } | { ok: false; error: string } {
  if (!data || typeof data !== 'object') return { ok: false, error: 'Column must be an object' };
  const d = data as Record<string, unknown>;

  if (typeof d['id'] !== 'string') return { ok: false, error: 'Column id must be a string' };
  if (!Array.isArray(d['tiles']))
    return { ok: false, error: `Column ${d['id']}: tiles must be an array` };
  for (const t of d['tiles'] as unknown[]) {
    if (typeof t !== 'string')
      return { ok: false, error: `Column ${d['id']}: tile IDs must be strings` };
  }

  return { ok: true, column: { id: d['id'] as string, tiles: d['tiles'] as string[] } };
}

// ---------------------------------------------------------------------------
// Commit validation (cross-reference checks, no neutral tiles)
// ---------------------------------------------------------------------------

export function validateForCommit(level: IconsMatchLevel): { ok: true } | { ok: false; errors: string[] } {
  const errors: string[] = [];

  if (level.goals.length === 0) errors.push('Level must have at least one goal');

  if (level.columns.length !== level.columnCount)
    errors.push(`Column count mismatch: expected ${level.columnCount}, got ${level.columns.length}`);

  // Collect all goal tile IDs and check for duplicates
  const goalTileIds = new Map<string, string>(); // tileId → goalId
  for (const goal of level.goals) {
    if (goal.tiles.length !== goal.size)
      errors.push(`Goal ${goal.id}: size (${goal.size}) does not match tile count (${goal.tiles.length})`);
    for (const tile of goal.tiles) {
      if (goalTileIds.has(tile.id))
        errors.push(`Duplicate tile id: ${tile.id}`);
      goalTileIds.set(tile.id, goal.id);
    }
  }

  // Collect all column tile IDs
  const columnTileIds = new Set<string>();
  for (const col of level.columns) {
    for (const tileId of col.tiles) {
      if (!goalTileIds.has(tileId))
        errors.push(`Tile ${tileId} in column ${col.id} is not assigned to any goal`);
      columnTileIds.add(tileId);
    }
  }

  // Every goal tile must appear in exactly one column
  for (const goal of level.goals) {
    for (const tile of goal.tiles) {
      if (!columnTileIds.has(tile.id))
        errors.push(`Tile ${tile.id} (${tile.imageId || tile.id}) in goal ${goal.id} is missing from all columns`);
    }
  }

  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}

// ---------------------------------------------------------------------------
// Google Sheets TSV import
//
// Format (tab-separated, copied from Google Sheets):
//
//   column_1  column_2  goal_1          goal_2
//   dolphin   fish      Kinds of fish   Different trees
//   car       monkey    dolphin         tree
//   cat       engine    fish            trunc
//
// Rules:
//   - Headers starting with "column_" = delivery columns (left to right)
//   - Headers starting with "goal_"   = goals (gameplay order)
//   - For goal columns: row[1] = title, rows[2..] = tile imageIds
//   - For column columns: rows[1..] = tile imageIds (top to bottom)
//   - columnCount and traySize = number of column_ headers
//   - Missing imageId/title → shown as ID in export, parsed as ID in import
// ---------------------------------------------------------------------------

export function parseTableImport(
  tsv: string,
): { ok: true; level: IconsMatchLevel } | { ok: false; error: string } {
  const rows = tsv
    .trim()
    .split('\n')
    .map(r => r.split('\t').map(c => c.trim()));

  if (rows.length < 2) return { ok: false, error: 'Table must have at least 2 rows (header + data)' };

  const headers = rows[0];
  const dataRows = rows.slice(1);

  const columnIndices: number[] = [];
  const goalIndices: number[] = [];

  for (let i = 0; i < headers.length; i++) {
    if (headers[i].startsWith('column_')) columnIndices.push(i);
    else if (headers[i].startsWith('goal_')) goalIndices.push(i);
  }

  if (columnIndices.length === 0) return { ok: false, error: 'No column_ headers found' };
  if (goalIndices.length === 0) return { ok: false, error: 'No goal_ headers found' };

  // Parse goals — tiles get sequential IDs assigned here
  let tileCounter = 1;
  const goals: LevelGoal[] = [];

  for (let gi = 0; gi < goalIndices.length; gi++) {
    const colIdx = goalIndices[gi];
    const goalNum = gi + 1;
    const titleCell = dataRows[0]?.[colIdx] ?? '';
    const title = titleCell || `goal_${goalNum}`;

    const tiles: LevelTile[] = [];
    for (let r = 1; r < dataRows.length; r++) {
      const cell = dataRows[r]?.[colIdx] ?? '';
      if (!cell) continue;
      tiles.push({ id: `tile_${tileCounter++}`, imageId: cell });
    }

    goals.push({ id: `goal_${goalNum}`, size: tiles.length, title, tiles });
  }

  // Build imageId → queue of tile IDs for column matching
  const tileQueue = new Map<string, string[]>();
  for (const goal of goals) {
    for (const tile of goal.tiles) {
      if (!tileQueue.has(tile.imageId)) tileQueue.set(tile.imageId, []);
      tileQueue.get(tile.imageId)!.push(tile.id);
    }
  }

  // Parse columns — match imageIds to goal tile IDs
  const columns: LevelColumn[] = [];

  for (let ci = 0; ci < columnIndices.length; ci++) {
    const colIdx = columnIndices[ci];
    const tileIds: string[] = [];

    for (let r = 0; r < dataRows.length; r++) {
      const cell = dataRows[r]?.[colIdx] ?? '';
      if (!cell) continue;
      const queue = tileQueue.get(cell);
      if (queue && queue.length > 0) {
        tileIds.push(queue.shift()!);
      }
      // Unmatched cells (imageId not in any goal) are silently skipped —
      // the editor will show them as neutral tiles and block commit.
    }

    columns.push({ id: `column_${ci + 1}`, tiles: tileIds });
  }

  const columnCount = columnIndices.length;

  return {
    ok: true,
    level: {
      id: 'level_1',
      columnCount,
      traySize: columnCount,
      goals,
      columns,
    },
  };
}

// ---------------------------------------------------------------------------
// Google Sheets TSV export
// ---------------------------------------------------------------------------

export function serializeToTable(level: IconsMatchLevel): string {
  // Build tile lookup: tileId → { tile, goalIndex }
  const tileById = new Map<string, { tile: LevelTile; goalIndex: number }>();
  level.goals.forEach((goal, gi) => {
    goal.tiles.forEach(tile => tileById.set(tile.id, { tile, goalIndex: gi }));
  });

  const maxColRows = Math.max(...level.columns.map(c => c.tiles.length), 0);
  const maxGoalTileRows = Math.max(...level.goals.map(g => g.tiles.length), 0);
  // goal rows = 1 (title) + tile rows
  const totalRows = Math.max(maxColRows, maxGoalTileRows + 1);

  const headers = [
    ...level.columns.map(c => c.id),
    ...level.goals.map(g => g.id),
  ];

  const output: string[][] = [headers];

  for (let r = 0; r < totalRows; r++) {
    const row: string[] = [];

    // Column cells: imageId of the tile at position r (empty if no tile)
    for (const col of level.columns) {
      const tileId = col.tiles[r];
      if (tileId) {
        const entry = tileById.get(tileId);
        row.push(entry ? (entry.tile.imageId || tileId) : tileId);
      } else {
        row.push('');
      }
    }

    // Goal cells: r === 0 → title, r > 0 → tile imageId at index r-1
    for (const goal of level.goals) {
      if (r === 0) {
        row.push(goal.title || goal.id);
      } else {
        const tile = goal.tiles[r - 1];
        row.push(tile ? (tile.imageId || tile.id) : '');
      }
    }

    output.push(row);
  }

  return output.map(r => r.join('\t')).join('\n');
}

// ---------------------------------------------------------------------------
// EditorDraft — editor working state (may contain neutral tiles)
// Stored in localStorage. Never committed to GitHub directly.
// ---------------------------------------------------------------------------

export interface EditorDraftTile {
  id: string;
  imageId: string;
  goalId: string | null;  // null = neutral (not yet assigned to any goal)
}

export interface EditorDraftGoal {
  id: string;
  order: number;   // gameplay sequence; goals sorted by this value
  title: string;
  tileIds: string[];
}

export interface EditorDraftColumn {
  id: string;
  tileIds: string[];  // first = topmost (delivered first in play)
}

export interface EditorDraft {
  levelId: string;
  columnCount: number;
  traySize: number;
  goals: EditorDraftGoal[];
  columns: EditorDraftColumn[];
  tiles: EditorDraftTile[];
}

export function draftFromLevel(level: IconsMatchLevel): EditorDraft {
  const tiles: EditorDraftTile[] = [];
  level.goals.forEach(goal => {
    goal.tiles.forEach(tile => {
      tiles.push({ id: tile.id, imageId: tile.imageId, goalId: goal.id });
    });
  });
  return {
    levelId: level.id,
    columnCount: level.columnCount,
    traySize: level.traySize,
    goals: level.goals.map((g, i) => ({
      id: g.id,
      order: i + 1,
      title: g.title,
      tileIds: g.tiles.map(t => t.id),
    })),
    columns: level.columns.map(c => ({ id: c.id, tileIds: [...c.tiles] })),
    tiles,
  };
}

export function levelFromDraft(
  draft: EditorDraft,
): { ok: true; level: IconsMatchLevel } | { ok: false; errors: string[] } {
  const tileMap = new Map(draft.tiles.map(t => [t.id, t]));
  const errors: string[] = [];

  // Check for neutral tiles in columns
  const assignedIds = new Set(draft.tiles.filter(t => t.goalId !== null).map(t => t.id));
  for (const col of draft.columns) {
    for (const tileId of col.tileIds) {
      if (!assignedIds.has(tileId)) {
        const neutralIdx = (() => {
          let n = 0;
          for (const c of draft.columns) {
            for (const id of c.tileIds) {
              if (!assignedIds.has(id)) n++;
              if (id === tileId) return n;
            }
          }
          return n;
        })();
        errors.push(`U${neutralIdx} (${tileId}) in column ${col.id} is not assigned to any goal`);
      }
    }
  }

  if (errors.length > 0) return { ok: false, errors };

  const sortedGoals = [...draft.goals].sort((a, b) => a.order - b.order);
  const goals: LevelGoal[] = sortedGoals.map(g => {
    const tiles: LevelTile[] = g.tileIds.map(tid => {
      const t = tileMap.get(tid);
      return { id: tid, imageId: t?.imageId ?? '' };
    });
    return { id: g.id, size: tiles.length, title: g.title, tiles };
  });

  const columns: LevelColumn[] = draft.columns.map(c => ({
    id: c.id,
    tiles: [...c.tileIds],
  }));

  const candidate: IconsMatchLevel = {
    id: draft.levelId,
    columnCount: draft.columnCount,
    traySize: draft.traySize,
    goals,
    columns,
  };

  const validation = validateForCommit(candidate);
  if (!validation.ok) return { ok: false, errors: validation.errors };

  return { ok: true, level: candidate };
}
